import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/LocalLeadersTable.css";
import { useNavigate } from "react-router-dom";
import { fileAPI, localLeadersAPI } from "../../api/index"; // adjust path to match your project
import { MANDAL_VILLAGES } from "./LocalLeadersForm"; // reuse the same mandal/village list — keeps them in sync

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

// NOTE: the backend only returns mandal_id/village_id on GET, not names.
// We don't have a real mandal-id -> name map here (only the name list used
// for create/update), so mandal/village display below is a best-effort
// placeholder until the API exposes names directly in LocalLeaderOut.
const PAGE_SIZE = 10;

const LocalLeadersTable = () => {
  const navigate = useNavigate();

  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- FETCH ----------
  const fetchLeaders = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await localLeadersAPI.getAll({});
      // Backend returns a PaginatedResponse: { items, total, page, page_size, pages }
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setLeaders(data);
    } catch (err) {
      console.error("Failed to load local leaders:", err);
      setLoadError(err.response?.data?.detail || "Could not load local leaders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaders();
  }, [fetchLeaders]);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  // ---------- SEARCH + FILTER (client-side) ----------
  const filteredLeaders = leaders.filter((item) => {
    const searchLower = search.trim().toLowerCase();
    const matchesSearch =
      searchLower === "" ||
      (item.leader_name || "").toLowerCase().includes(searchLower) ||
      (item.position || "").toLowerCase().includes(searchLower);

    const matchesStatus =
      statusFilter === "All Status" || (item.status || "").toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredLeaders.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageLeaders = filteredLeaders.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- STATS ----------
  const totalLeaders = leaders.length;
  const activeLeaders = leaders.filter((l) => l.status === "active").length;
  const inactiveLeaders = leaders.filter((l) => l.status === "inactive").length;
  const totalMandals = new Set(leaders.map((l) => l.mandal_id).filter((v) => v != null)).size;

  const activePct = totalLeaders ? ((activeLeaders / totalLeaders) * 100).toFixed(2) : 0;
  const inactivePct = totalLeaders ? ((inactiveLeaders / totalLeaders) * 100).toFixed(2) : 0;

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({
      id: item.id,
      name: item.leader_name || "",
      alias: item.alias_name || "",
      position: item.position || "",
      party: item.party || "",
      voterId: item.epic_no || "",
      aadhaar: "", // never pre-filled — backend only returns a masked value
      mobile: item.mobile_number || "",
      mandal: "", // backend doesn't return mandal name, only mandal_id
      village: "", // same for village
      dateJoined: item.date_joined || "",
      status: capitalize(item.status),
      remarks: item.remarks || "",
      photo_url: item.photo_url || null,
    });
    setEditFile(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditFile(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "mandal") {
      const firstVillage = MANDAL_VILLAGES[value]?.[0] || "";
      setEditItem((prev) => ({ ...prev, mandal: value, village: firstVillage }));
      return;
    }

    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setEditFile(selected);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let photoUrl = editItem.photo_url;
      if (editFile) {
        const uploadRes = await fileAPI.upload(editFile, "local_leaders");
        photoUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          photoUrl;
      }

      await localLeadersAPI.update(editItem.id, {
        leader_name: editItem.name,
        alias_name: editItem.alias || null,
        position: editItem.position,
        party: editItem.party,
        epic_no: editItem.voterId || null,
        // only send a new Aadhaar if the user actually typed one — otherwise
        // omit it so the existing encrypted value isn't wiped
        aadhaar_number: editItem.aadhaar || undefined,
        mobile_number: editItem.mobile || null,
        // village_name/mandal_name only sent if the user picked something —
        // otherwise we'd overwrite the current geography with nothing,
        // since the backend can't tell us the current names to prefill.
        village_name: editItem.village || undefined,
        mandal_name: editItem.mandal || undefined,
        date_joined: editItem.dateJoined,
        status: editItem.status.toLowerCase(),
        remarks: editItem.remarks || null,
        photo_url: photoUrl,
      });

      await fetchLeaders();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update leader:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- DELETE LOGIC ----------
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await localLeadersAPI.delete(deleteId);
      await fetchLeaders();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete leader:", err);
      alert(err.response?.data?.detail || "Could not delete this leader (super admin only).");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="ldr123-page">
      {/* Header */}
      <div className="ldr123-header">
        <div>
          <h2 className="ldr123-title">Local Political Leaders</h2>
          <p className="ldr123-subtitle">
            Manage and view local political leaders in your constituency
          </p>
        </div>

        <button
          className="ldr123-add-btn"
          onClick={() => navigate("/dashboard/LocalLeadersForm")}
        >
          <span className="ldr123-add-icon">+</span> Add New Leader
        </button>
      </div>

      {loadError && <div className="ldr123-no-data">{loadError}</div>}

      {/* Stat Cards */}
      <div className="ldr123-stats-grid">
        <div className="ldr123-stat-card">
          <div className="ldr123-stat-icon ldr123-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
              <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              <circle cx="17" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.7" />
              <path d="M15.5 14.7c2.6.4 4.5 2.3 4.5 5.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </div>
          <div className="ldr123-stat-info">
            <span className="ldr123-stat-label">Total Leaders</span>
            <span className="ldr123-stat-value">{totalLeaders}</span>
            <span className="ldr123-stat-sub">All Leaders</span>
          </div>
        </div>

        <div className="ldr123-stat-card">
          <div className="ldr123-stat-icon ldr123-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.7" />
              <path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              <path d="M9.5 9l1.8 1.8L15 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="ldr123-stat-info">
            <span className="ldr123-stat-label">Active Leaders</span>
            <span className="ldr123-stat-value">{activeLeaders}</span>
            <span className="ldr123-stat-sub ldr123-sub-green">{activePct}% of total</span>
          </div>
        </div>

        <div className="ldr123-stat-card">
          <div className="ldr123-stat-icon ldr123-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.7" />
              <path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              <path d="M9.5 7l4 4M13.5 7l-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </div>
          <div className="ldr123-stat-info">
            <span className="ldr123-stat-label">Inactive Leaders</span>
            <span className="ldr123-stat-value">{inactiveLeaders}</span>
            <span className="ldr123-stat-sub ldr123-sub-orange">{inactivePct}% of total</span>
          </div>
        </div>

        <div className="ldr123-stat-card">
          <div className="ldr123-stat-icon ldr123-icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="ldr123-stat-info">
            <span className="ldr123-stat-label">Distinct Mandal IDs</span>
            <span className="ldr123-stat-value">{totalMandals}</span>
            <span className="ldr123-stat-sub">Kandukur Constituency</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="ldr123-filters">
        <div className="ldr123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search leader by name, position..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="ldr123-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>

        <button className="ldr123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Reset Filters
        </button>
      </div>

      {/* Table */}
      <div className="ldr123-table-wrapper">
        <table className="ldr123-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Photo</th>
              <th>Name</th>
              <th>Position</th>
              <th>Village</th>
              <th>Mandal</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="ldr123-no-data">Loading...</td>
              </tr>
            ) : pageLeaders.length === 0 ? (
              <tr>
                <td colSpan="9" className="ldr123-no-data">
                  No leaders found.
                </td>
              </tr>
            ) : (
              pageLeaders.map((item, index) => (
                <tr key={item.id}>
                  <td>{startIndex + index + 1}</td>

                  <td>
                    {item.photo_url ? (
                      <img
                        src={item.photo_url}
                        alt={item.leader_name}
                        className="ldr123-photo"
                      />
                    ) : (
                      <span className="ldr123-no-photo">—</span>
                    )}
                  </td>

                  <td>
                    <div className="ldr123-name-cell">
                      <span className="ldr123-name-text">{item.leader_name}</span>
                      {item.alias_name && (
                        <span className="ldr123-alias-text">{item.alias_name}</span>
                      )}
                    </div>
                  </td>

                  <td>{item.position || "—"}</td>

                  <td>
                    <div className="ldr123-place-cell">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                      </svg>
                      {/* Backend only returns village_id, not the name — see
                          note at top of file about why this can't resolve yet */}
                      {item.village_id != null ? `Village #${item.village_id}` : "—"}
                    </div>
                  </td>

                  <td>{item.mandal_id != null ? `Mandal #${item.mandal_id}` : "—"}</td>

                  <td>
                    <div className="ldr123-contact-cell">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2.1L8 9.7a16 16 0 006 6l1.2-1.2a2 2 0 012.1-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                      </svg>
                      {item.mobile_number || "—"}
                    </div>
                  </td>

                  <td>
                    <span
                      className={`ldr123-status-badge ${
                        item.status === "active"
                          ? "ldr123-status-active"
                          : "ldr123-status-inactive"
                      }`}
                    >
                      {item.status === "active" ? (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
                          <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
                          <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                      )}
                      {capitalize(item.status)}
                    </span>
                  </td>

                  <td>
                    <div className="ldr123-action-cell">
                      <button
                        className="ldr123-edit-btn"
                        onClick={() => openEditModal(item)}
                        title="Edit"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <button
                        className="ldr123-delete-btn"
                        onClick={() => openDeleteModal(item.id)}
                        title="Delete"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="ldr123-pagination-bar">
        <span className="ldr123-pagination-info">
          Showing {pageLeaders.length === 0 ? 0 : startIndex + 1} to{" "}
          {startIndex + pageLeaders.length} of {filteredLeaders.length} leaders
        </span>

        <div className="ldr123-pagination-controls">
          <button
            className="ldr123-page-arrow"
            onClick={() => handlePageChange(safePage - 1)}
          >
            &lt;
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`ldr123-page-num ${safePage === p ? "ldr123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button
            className="ldr123-page-arrow"
            onClick={() => handlePageChange(safePage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditModal && editItem && (
        <div className="ldr123-modal-overlay" onClick={closeEditModal}>
          <div className="ldr123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ldr123-modal-header">
              <h3>Edit Leader</h3>
              <button className="ldr123-modal-close" onClick={closeEditModal}>
                &times;
              </button>
            </div>

            <form className="ldr123-modal-body" onSubmit={handleEditSave}>
              <div className="ldr123-form-row">
                <div className="ldr123-form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editItem.name}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="ldr123-form-group">
                  <label>Alias</label>
                  <input
                    type="text"
                    name="alias"
                    value={editItem.alias}
                    onChange={handleEditChange}
                  />
                </div>
              </div>

              <div className="ldr123-form-group">
                <label>Position</label>
                <input
                  type="text"
                  name="position"
                  value={editItem.position}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="ldr123-form-row">
                <div className="ldr123-form-group">
                  <label>Party</label>
                  <input
                    type="text"
                    name="party"
                    value={editItem.party}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="ldr123-form-group">
                  <label>Voter ID</label>
                  <input
                    type="text"
                    name="voterId"
                    value={editItem.voterId}
                    onChange={handleEditChange}
                  />
                </div>
              </div>

              <div className="ldr123-form-row">
                <div className="ldr123-form-group">
                  <label>Replace Aadhaar (leave blank to keep current)</label>
                  <input
                    type="text"
                    name="aadhaar"
                    maxLength="12"
                    placeholder="Only fill in to change"
                    value={editItem.aadhaar}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="ldr123-form-group">
                  <label>Mobile</label>
                  <input
                    type="text"
                    name="mobile"
                    value={editItem.mobile}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>

              <div className="ldr123-form-row">
                <div className="ldr123-form-group">
                  <label>Mandal (leave blank to keep current)</label>
                  <select
                    name="mandal"
                    value={editItem.mandal}
                    onChange={handleEditChange}
                  >
                    <option value="">Keep current</option>
                    {Object.keys(MANDAL_VILLAGES).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ldr123-form-group">
                  <label>Village</label>
                  <select
                    name="village"
                    value={editItem.village}
                    onChange={handleEditChange}
                    disabled={!editItem.mandal}
                  >
                    <option value="">
                      {editItem.mandal ? "Select Village" : "Choose mandal first"}
                    </option>
                    {(MANDAL_VILLAGES[editItem.mandal] || []).map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ldr123-form-row">
                <div className="ldr123-form-group">
                  <label>Date Joined</label>
                  <input
                    type="date"
                    name="dateJoined"
                    value={editItem.dateJoined}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="ldr123-form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={editItem.status}
                    onChange={handleEditChange}
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="ldr123-form-group">
                <label>Replace Photo (optional)</label>
                <input type="file" accept="image/*" onChange={handleEditFileChange} />
              </div>

              <div className="ldr123-form-group">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  rows="3"
                  value={editItem.remarks}
                  onChange={handleEditChange}
                />
              </div>

              <div className="ldr123-modal-footer">
                <button
                  type="button"
                  className="ldr123-btn-cancel"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="ldr123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- DELETE CONFIRM MODAL ---------------- */}
      {showDeleteModal && (
        <div className="ldr123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="ldr123-modal ldr123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ldr123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="ldr123-confirm-title">Delete this leader?</h3>
            <p className="ldr123-confirm-text">
              This action cannot be undone. Are you sure you want to delete
              this leader?
            </p>

            <div className="ldr123-confirm-footer">
              <button className="ldr123-btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button className="ldr123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalLeadersTable;